/**
 * @file GameWiki 跳转：消费 URL kbId / agentId，预填知识库 @ 与 Agent 默认选中
 */
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import {
  useSearchParamsAppoint,
} from '@/pages/aigc/hooks/useQuery';
import { DATABRAIN_AGENT_ID2 } from '../../config/constant';
import { useKnowledgeBaseList } from '../../components/QAInput/KnowledgeBaseMention/hooks/useKnowledgeBaseApi';
import {
  collectSelectedKnowledgeByKbIds,
  parseAgentId,
  parseKbIds,
  stripKbPrefillSearchParams,
} from '../../components/QAInput/KnowledgeBaseMention/utils/knowledgeBaseUtils';
import type { ISelectedKnowledgeTagData } from '../../components/QAInput/KnowledgeBaseMention/components/SelectedKnowledgeTag';
import {
  KB_PREFILL_PLACEHOLDER_SERIALIZED,
  serializeKbPrefill,
} from '../../components/QAInput/utils/inlineMentionRanges';

/** Hook 返回值 */
export interface IUseHomeUrlKbPrefillResult {
  selectedKnowledge: ISelectedKnowledgeTagData[];
  setSelectedKnowledge: (items: ISelectedKnowledgeTagData[]) => void;
  isKbPrefillPending: boolean;
  kbPrefillSerialized: string | undefined;
  /** URL agentId 优先；仅 kbId 时默认 2.0；无外链参数为 undefined */
  urlForcedAgentId: string | undefined;
  /** 切换页签时调用，停止 kb 预填注入 */
  clearKbPrefill: () => void;
}

/**
 * 用 useKnowledgeBaseList 拉库列表，按 URL kbId 预填整库 @库名（不必等文档）
 * @returns 已选知识库、序列化串、URL 强制 Agent 等
 */
const useHomeUrlKbPrefill = (): IUseHomeUrlKbPrefillResult => {
  const { formatMessage } = useIntl();
  const { kbId: kbIdQuery, agentId: agentIdQuery } = useSearchParamsAppoint();
  const [pendingKbIds] = useState(() => parseKbIds(kbIdQuery));
  const [urlAgentId] = useState(() => parseAgentId(agentIdQuery));
  const [selectedKnowledge, setSelectedKnowledge] = useState<ISelectedKnowledgeTagData[] | undefined>();
  const [kbPrefillCancelled, setKbPrefillCancelled] = useState(false);

  // 用 replaceState 清除 kbId/agentId，避免 push 新 history 记录与发送跳转竞争
  useEffect(() => {
    if (!pendingKbIds.length && !urlAgentId) {
      return;
    }
    const nextUrl = stripKbPrefillSearchParams(
      window.location.search,
      window.location.pathname,
      window.location.hash,
    );
    window.history.replaceState(window.history.state, '', nextUrl);
  }, [pendingKbIds.length, urlAgentId]);

  const { kbList, listLoading } = useKnowledgeBaseList({
    ready: pendingKbIds.length > 0,
  });

  const isKbPrefillPending = pendingKbIds.length > 0 && listLoading && !kbPrefillCancelled;
  const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });

  const urlSelectedKnowledge = useMemo(() => {
    if (!pendingKbIds.length || listLoading) {
      return [];
    }
    return collectSelectedKnowledgeByKbIds(kbList, pendingKbIds);
  }, [pendingKbIds, kbList, listLoading]);

  const kbPrefillSerialized = useMemo(() => {
    if (!pendingKbIds.length || kbPrefillCancelled) {
      return undefined;
    }
    if (listLoading) {
      return KB_PREFILL_PLACEHOLDER_SERIALIZED;
    }
    if (!urlSelectedKnowledge.length) {
      return '';
    }
    return serializeKbPrefill(urlSelectedKnowledge, kbDocUnit);
  }, [pendingKbIds.length, kbPrefillCancelled, listLoading, urlSelectedKnowledge, kbDocUnit]);

  const urlForcedAgentId = useMemo(() => {
    if (urlAgentId) {
      return urlAgentId;
    }
    if (pendingKbIds.length > 0) {
      return DATABRAIN_AGENT_ID2;
    }
    return undefined;
  }, [urlAgentId, pendingKbIds.length]);

  return {
    selectedKnowledge: selectedKnowledge ?? urlSelectedKnowledge,
    setSelectedKnowledge,
    isKbPrefillPending,
    kbPrefillSerialized,
    urlForcedAgentId,
    clearKbPrefill: () => {
      setKbPrefillCancelled(true);
      setSelectedKnowledge([]);
    },
  };
};

export default useHomeUrlKbPrefill;
